/**
 * Professional Voice Text-to-Speech Service
 * Handles Web Speech API for text-to-speech with browser compatibility
 * 
 * @author Software Engineering Team
 * @version 1.0.0
 */

class VoiceTTSService {
  constructor() {
    this.synth = null;
    this.currentUtterance = null;
    this.isInitialized = false;
    this.isPlaying = false;
    this.isPaused = false;
    this.voices = [];
    
    // Configuration
    this.config = {
      rate: 1.0,           // Speech rate (0.1 to 10)
      pitch: 1.0,          // Speech pitch (0 to 2)
      volume: 1.0,         // Speech volume (0 to 1)
      lang: 'en-US',       // Default language
      voiceURI: null       // Preferred voice URI
    };
    
    // Event callbacks
    this.callbacks = {
      onStart: null,
      onEnd: null,
      onError: null,
      onPause: null,
      onResume: null,
      onBoundary: null
    };
    
    this.initialize();
  }

  /**
   * Initialize the speech synthesis service
   * @returns {Promise<boolean>} - Success status
   */
  async initialize() {
    try {
      // Check browser compatibility
      if (!this.isSpeechSynthesisSupported()) {
        throw new Error('Speech synthesis not supported in this browser');
      }

      this.synth = window.speechSynthesis;
      
      // Load voices (may be async in some browsers)
      await this.loadVoices();
      
      this.isInitialized = true;
      console.info('VoiceTTSService initialized successfully');
      return true;
      
    } catch (error) {
      console.error('Failed to initialize VoiceTTSService:', error);
      return false;
    }
  }

  /**
   * Check if speech synthesis is supported
   * @returns {boolean} - Support status
   */
  isSpeechSynthesisSupported() {
    return 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
  }

  /**
   * Load available voices
   * @returns {Promise<Array>} - Available voices
   */
  async loadVoices() {
    return new Promise((resolve) => {
      // Voices might load asynchronously
      const loadVoicesSync = () => {
        this.voices = this.synth.getVoices();
        console.info(`Loaded ${this.voices.length} voices`);
        resolve(this.voices);
      };

      // Check if voices are already loaded
      if (this.synth.getVoices().length > 0) {
        loadVoicesSync();
      } else {
        // Wait for voices to load
        this.synth.addEventListener('voiceschanged', loadVoicesSync, { once: true });
        
        // Fallback timeout
        setTimeout(() => {
          if (this.voices.length === 0) {
            loadVoicesSync();
          }
        }, 1000);
      }
    });
  }

  /**
   * Get available voices filtered by language
   * @param {string} language - Language code (optional)
   * @returns {Array} - Available voices
   */
  getVoices(language = null) {
    if (!language) return this.voices;
    return this.voices.filter(voice => voice.lang.startsWith(language.split('-')[0]));
  }

  /**
   * Get the best voice for the current language
   * @returns {SpeechSynthesisVoice|null} - Best available voice
   */
  getBestVoice() {
    if (this.voices.length === 0) return null;

    // If a specific voice is configured, use it
    if (this.config.voiceURI) {
      const preferredVoice = this.voices.find(voice => voice.voiceURI === this.config.voiceURI);
      if (preferredVoice) return preferredVoice;
    }

    // Find voices for current language
    const langVoices = this.getVoices(this.config.lang);
    if (langVoices.length > 0) {
      // Prefer local voices over remote ones
      const localVoices = langVoices.filter(voice => voice.localService);
      if (localVoices.length > 0) {
        return localVoices[0];
      }
      return langVoices[0];
    }

    // Fallback to any available voice
    return this.voices[0];
  }

  /**
   * Preprocess text for better speech synthesis
   * @param {string} text - Raw text
   * @returns {string} - Processed text
   */
  preprocessText(text) {
    if (!text || typeof text !== 'string') return '';

    let processed = text;

    // Remove markdown formatting
    processed = processed.replace(/\*\*(.*?)\*\*/g, '$1'); // Bold
    processed = processed.replace(/\*(.*?)\*/g, '$1'); // Italic
    processed = processed.replace(/`(.*?)`/g, '$1'); // Code
    processed = processed.replace(/#{1,6}\s*/g, ''); // Headers
    processed = processed.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Links
    
    // Clean up bullet points
    processed = processed.replace(/^[\s]*[-*•]\s+/gm, ''); // Remove bullet markers
    
    // Remove extra whitespace
    processed = processed.replace(/\n{3,}/g, '\n\n'); // Collapse multiple newlines
    processed = processed.replace(/\s+/g, ' '); // Normalize spaces
    
    // Add pauses for better speech flow
    processed = processed.replace(/\n\n/g, '. '); // Convert double newlines to pauses
    processed = processed.replace(/\n/g, ' '); // Convert single newlines to spaces
    
    return processed.trim();
  }

  /**
   * Speak the given text
   * @param {string} text - Text to speak
   * @returns {Promise<void>}
   */
  async speak(text) {
    try {
      if (!this.isInitialized) {
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error('Failed to initialize speech synthesis');
        }
      }

      // Stop any current speech
      this.stop();

      // Preprocess text
      const processedText = this.preprocessText(text);
      if (!processedText) {
        throw new Error('No text to speak');
      }

      // Create utterance
      this.currentUtterance = new SpeechSynthesisUtterance(processedText);
      this.configureUtterance();
      this.setupUtteranceEvents();

      // Start speaking
      this.synth.speak(this.currentUtterance);
      this.isPlaying = true;
      this.isPaused = false;
      
      console.info('Started speaking:', processedText.substring(0, 50) + '...');

    } catch (error) {
      console.error('Failed to start speech:', error);
      this.callbacks.onError?.(error);
      throw error;
    }
  }

  /**
   * Configure utterance properties
   * @private
   */
  configureUtterance() {
    if (!this.currentUtterance) return;

    this.currentUtterance.rate = this.config.rate;
    this.currentUtterance.pitch = this.config.pitch;
    this.currentUtterance.volume = this.config.volume;
    this.currentUtterance.lang = this.config.lang;

    // Set voice
    const voice = this.getBestVoice();
    if (voice) {
      this.currentUtterance.voice = voice;
      console.info('Using voice:', voice.name);
    }
  }

  /**
   * Setup utterance event listeners
   * @private
   */
  setupUtteranceEvents() {
    if (!this.currentUtterance) return;

    this.currentUtterance.onstart = () => {
      this.isPlaying = true;
      this.isPaused = false;
      console.info('Speech started');
      this.callbacks.onStart?.();
    };

    this.currentUtterance.onend = () => {
      this.isPlaying = false;
      this.isPaused = false;
      console.info('Speech ended');
      this.callbacks.onEnd?.();
    };

    this.currentUtterance.onerror = (event) => {
      this.isPlaying = false;
      this.isPaused = false;
      console.error('Speech error:', event.error);
      this.callbacks.onError?.(new Error(`Speech synthesis error: ${event.error}`));
    };

    this.currentUtterance.onpause = () => {
      this.isPaused = true;
      console.info('Speech paused');
      this.callbacks.onPause?.();
    };

    this.currentUtterance.onresume = () => {
      this.isPaused = false;
      console.info('Speech resumed');
      this.callbacks.onResume?.();
    };

    this.currentUtterance.onboundary = (event) => {
      this.callbacks.onBoundary?.(event);
    };
  }

  /**
   * Pause speech
   */
  pause() {
    if (this.synth && this.isPlaying && !this.isPaused) {
      this.synth.pause();
      console.info('Speech paused');
    }
  }

  /**
   * Resume speech
   */
  resume() {
    if (this.synth && this.isPlaying && this.isPaused) {
      this.synth.resume();
      console.info('Speech resumed');
    }
  }

  /**
   * Stop speech
   */
  stop() {
    if (this.synth) {
      this.synth.cancel();
      this.isPlaying = false;
      this.isPaused = false;
      this.currentUtterance = null;
      console.info('Speech stopped');
    }
  }

  /**
   * Set event callbacks
   * @param {Object} callbacks - Event callbacks
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Update configuration
   * @param {Object} newConfig - New configuration options
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    console.info('TTS config updated:', this.config);
  }

  /**
   * Get current status
   * @returns {Object} - Service status
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isPlaying: this.isPlaying,
      isPaused: this.isPaused,
      isSupported: this.isSpeechSynthesisSupported(),
      voicesAvailable: this.voices.length,
      config: this.config
    };
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.stop();
    this.synth = null;
    this.currentUtterance = null;
    this.isInitialized = false;
    this.callbacks = {};
    console.info('VoiceTTSService destroyed');
  }
}

export default VoiceTTSService;
